interface ReturnsChartTooltipPayload {
    periodLabel: string;
    value: number | null;
    mwrr?: number | null;
}

interface ReturnsChartTooltipProps {
    /** Whether the tooltip is active (from Recharts) */
    active?: boolean;
    /** Payload data from Recharts */
    payload?: ReadonlyArray<{ payload?: ReturnsChartTooltipPayload }>;
}

/**
 * Format a percentage value for display.
 * Adds + sign for positive values.
 */
function formatPercentage(value: number | null | undefined): string {
    if (value === null || value === undefined) {
        return 'N/A';
    }
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
}

/**
 * Simple tooltip component for MWRR charts.
 *
 * Displays:
 * - Period label
 * - Single percentage value (e.g., "MWRR: +12.50%")
 * - Color-coded positive (green) / negative (red)
 *
 * No change/growth calculations are shown.
 */
export function ReturnsChartTooltip({ active, payload }: ReturnsChartTooltipProps) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    const data = payload[0]?.payload;
    if (!data) {
        return null;
    }

    const { periodLabel, value } = data;

    // Determine color based on value (positive = green, negative = red)
    const colorClass = value === null
        ? 'text-gray-500 dark:text-gray-400'
        : value >= 0
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary shadow-lg p-2 sm:p-3 sm:min-w-[140px]">
            {/* Period label */}
            <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1 sm:mb-2">
                {periodLabel}
            </p>

            {/* Desktop: labeled row */}
            <div className="hidden sm:flex items-center justify-between gap-3">
                <span className="text-sm text-gray-500 dark:text-gray-400">MWRR:</span>
                <span className={`font-semibold ${colorClass}`}>
                    {formatPercentage(value)}
                </span>
            </div>

            {/* Mobile: vertical stacked layout with label */}
            <div className="sm:hidden">
                <div className="text-xs text-gray-500 dark:text-gray-400">MWRR</div>
                <span className={`font-semibold ${colorClass}`}>
                    {formatPercentage(value)}
                </span>
            </div>
        </div>
    );
}
